import { LoadingButton } from '@mui/lab'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { track, useInitiatePayloadPayment, useSubmitPayloadPayment } from '../../../../logic'
import { useOptimisticUpdateNotificationAndOrderNotification, useOptimisticUpdatePayloadMetaData } from '../../../../logic/optimisticUpdates'
import { useGetOrder, useGetPayloadMetaData } from '../../../../logic/useApiQuery'
import { NumericFormat } from 'react-number-format'
import { usePayloadProcessingState } from '../../../context/payload-processing-state/PayloadProcessingState'
import { getTransactionsTotal } from '../../../../utils/getTransactionsTotal'
import { ErrorDialog } from '../../dialogs/ErrorDialog'
import { PortalOrder } from '../../../../types'

export function CashToCloseForm() {
  const { closingId, id } = useParams()
  const { search } = useLocation()
  const { data: orderData } = useGetOrder(closingId ?? '', search ?? '')

  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const { optimisticUpdatePayloadMetaData } = useOptimisticUpdatePayloadMetaData()

  const { mutate: mutateInitiatePayloadPayment, data: initiatePayloadPaymentData } = useInitiatePayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    useErrorBoundary: true,
    onSuccess: () => {
      setPaymentStatus('idle')
    }
  })
  const { mutate: mutateSubmitPayloadPayment } = useSubmitPayloadPayment(closingId ?? '', id ?? '', search ?? '', {
    onSuccess: (data) => {
      if (!id || !closingId) return
      optimisticUpdateNotificationAndOrderNotification({orderKey: closingId, notificationId: id, search, patch: { IsPaymentSubmitted: true } })
      optimisticUpdatePayloadMetaData({orderKey: closingId, notificationId: id, search, patch: { transactionId: data.transactionId, amount: data.amount, createdAt: data.createdAt, accountNumber: data.accountNumber, status: data.status }})
      setPayloadProcessing(false)
    },
    useErrorBoundary: true
  })

  const { data: transactions } = useGetPayloadMetaData(closingId ?? '', id ?? '', search ?? '')
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing'>('processing')
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const { setPayloadProcessing } = usePayloadProcessingState()
  const payloadFormRef = useRef(null)
  const plaidButtonRef = useRef(null)

  const [payloadError, setPayloadError] = useState<any>(null)
  const [amount, setAmount] = useState<string | number>(
    Math.max( 
      0,
      Number((orderData?.Order as PortalOrder).CashToCloseDetails?.amount) - getTransactionsTotal(transactions) //Just saying this is a PortalOrder to satisfy TS for now as AdhocPortalOrder is not relevant here.
    )
  )

  const amountGreaterThanZero = Number(amount) > 0
  const amountLessThan400K = Number(amount) <= 400000

  // Initialize Payload form after payment is initiated and token is available
  useEffect(() => {
    if (initiatePayloadPaymentData?.clientToken && payloadFormRef.current && amountGreaterThanZero && amountLessThan400K && termsAccepted) {
      Payload(initiatePayloadPaymentData?.clientToken)
      
      const form = new Payload.Form({
        form: payloadFormRef.current,
        preventDefaultOnSubmit: true
      })

      form.plaid(plaidButtonRef.current)
      //The following events are grabbed from here, but this documentation does not include 'closed'.
      //https://docs.payload.com/ui/payloadjs/secure-input/options/

      form.on('success', function(event: any) {
        mutateSubmitPayloadPayment({payloadTransactionId: event.transaction_id})
        mutateInitiatePayloadPayment(undefined)
      })

      form.on('processing', function() {
        setPayloadProcessing(true)
      })

      form.on('closed', function() {
        setPaymentStatus('idle')
      })
      form.on('error', function(error: any) {
        setPayloadError(error)
      })
    }
  }, [initiatePayloadPaymentData?.clientToken, mutateSubmitPayloadPayment, mutateInitiatePayloadPayment, setPayloadProcessing, amountGreaterThanZero, amountLessThan400K, termsAccepted])

  useEffect(() => {
    mutateInitiatePayloadPayment(undefined)
  }, [mutateInitiatePayloadPayment])

  const handleCompletePaymentClicked = () => {
    if (amountGreaterThanZero && amountLessThan400K) {
      setPaymentStatus('processing')
    }
  }

  const hasTransactions = (transactions?.length ?? 0) > 0;
  const paymentButtonText = hasTransactions ? 'Complete another payment' : 'Complete a payment'
  const paymentButtonVariant: 'text' | 'contained' = hasTransactions ? 'text' : 'contained'

  if (payloadError) {
    track('error', 'payload form', {}, {
      message: JSON.stringify(payloadError),
      component: 'CashToCloseForm',
    })
    return (
      <ErrorDialog error={'We were unable to process your payment.  Please try again.'}/>
    )
  }

  return (
    <>
      <>
        <form id="payload" ref={payloadFormRef} pl-form="payment"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
          <Box display="flex" flexDirection="column" gap={3} alignItems="center">
            <Box alignItems="center" display='flex' flexDirection='column'>
              <Box width={'150px'}>
                <Typography variant="subtitle2" gutterBottom>Amount</Typography>
                <NumericFormat
                  allowNegative={false}
                  allowLeadingZeros={false}
                  value={amount}
                  onValueChange={(values) => {
                    setAmount(values.value)
                  }}
                  thousandSeparator={true}
                  prefix="$"
                  pl-input="amount"
                  style={{
                    width: '100%',
                    minHeight: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    fontSize: '1rem',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                />
              </Box>
              {!amountGreaterThanZero && <Typography color='red' fontSize='15px'>Amount must be greater than $0</Typography>}
              {!amountLessThan400K && <Typography color='red' fontSize='15px'>Amount must be less than $400,000</Typography>}
            </Box>

            <FormControlLabel
              control={
                <Checkbox 
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              }
              label={
                <Typography fontSize='15px'>
                  By completing a payment you confirm that you have read, accepted and agreed to be bound by Payload's{' '}
                  <a
                    href='https://payload.com/terms'
                    target='_blank'
                    rel='noopener noreferrer'

                    style={{ color: '#1976d2', textDecoration: 'underline' }}
                  >Terms of Use</a>
                    .
                </Typography>
              }
              sx={{ marginX: 4 }}
            />

            <Box alignItems="center" display='flex' flexDirection='column'>
              <LoadingButton
                disabled={!amountGreaterThanZero || !amountLessThan400K || !termsAccepted}
                loading={paymentStatus === 'processing'}
                onClick={handleCompletePaymentClicked}
                ref={plaidButtonRef}
                variant={paymentButtonVariant}
                color="primary" 
                type="button"
              >
                {paymentButtonText}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </>
    </>
  )
}

// Add TypeScript declaration for Payload as a global variable
declare global {
  var Payload: any
}
